/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { type ConditionsWithData, checkConditions } from '@mail-core/std/fn/validate';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { duplicatesBanner, duplicatesDebugLogger, IS_DUPLICATES_ENABLED } from 'reactApp/appHelpers/featuresHelpers/features/duplicates';
import {
    freemiumConnectedBanner,
    freemiumDebugLogger,
    isHardFreemium,
    isSoftFreemium,
} from 'reactApp/appHelpers/featuresHelpers/features/freemium';
import { overquotaBackendEnabled, overquotaBannerConfig } from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import {
    preOverquotaBannerConfig,
    preOverquotaDebugLogger,
    preQuotaEnablePaid,
    preQuotaSizeLimit,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaPre';
import { overquotaDebugLogger } from 'reactApp/appHelpers/featuresHelpers/features/overquotaStates';
import { forceVigboLinkedAccount, vigboTariffBannerRemindDays, vigboTariffs } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { useIsFreemium } from 'reactApp/hooks/useFreemium';
import { getLatestExpiredSubscription } from 'reactApp/modules/billingData/billingData.helpers';
import { BillingDataSelectors, getBillingDataLoadingState } from 'reactApp/modules/billingData/billingData.selectors';
import { getUserHasDuplicates } from 'reactApp/modules/duplicates/duplicates.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { isOverquotaWithStatus } from 'reactApp/modules/userQuota/overquota.helpers';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { getVigboAlbums, getVigboLinkState, hasActiveVigboTariff } from 'reactApp/modules/vigbo/vigbo.selectors';
import { VigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.types';
import type { RootState } from 'reactApp/store';
import { DuplicatesPromoBanner } from 'reactApp/ui/Duplicates/DuplicatesPromoBanner/DuplicatesPromoBanner';
import { getDuplicatesPromoBannerHidden } from 'reactApp/ui/Duplicates/DuplicatesPromoBanner/DuplicatesPromoBanner.helpers';
import { FreemiumConnectedBanner } from 'reactApp/ui/FreemiumConnectedBanner/FreemiumConnectedBanner';
import { OverquotaBanner } from 'reactApp/ui/OverquotaBanner/OverquotaBanner';
import { PreOverquotaBanner } from 'reactApp/ui/PreOverquotaBanner/PreOverquotaBanner';
import { isPreOverquotaBannerPeriodPassedCheck } from 'reactApp/ui/PreOverquotaBanner/PreOverquotaBanner.helpers';
import { VigboEndTariffBanner } from 'reactApp/ui/VigboEndTariffBanner/VigboEndTariffBanner';
import { VigboRenewRemindBanner } from 'reactApp/ui/VigboRenewRemindBanner/VigboRenewRemindBanner';
import { ConditionsCheckType, getCheckAllConditions } from 'reactApp/utils/conditionChecker';

const duplicatesCheckAllConditionsTrue = getCheckAllConditions(duplicatesDebugLogger, ConditionsCheckType.FirstFalse);

export const useBanner = () => {
    const [show, setShow] = useState<boolean>(true);
    const isCorp = useSelector(UserSelectors.isCorpUser);
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const isOverquota = useSelector(isOverquotaWithStatus);
    const { used: usedPercent } = useSelector(UserQuotaSelectors.getFilledPercents);
    const storage = useSelector(getCurrentStorage);
    const dispatch = useDispatch();
    const { isAllDocuments, isFamilyPage, isFamilyAlbums, isFamilyFolders, isHome, isAlbums, isDuplicates } = getStorage(storage);
    const isFreemium = useIsFreemium();

    const isMobile = useSelector(EnvironmentSelectors.isMobile);
    const isVigboActiveTariff = useSelector(hasActiveVigboTariff);
    const { isLoading, isLoaded } = useSelector(getBillingDataLoadingState);
    const vigboAlbums = useSelector(getVigboAlbums);
    const vigboLinkState = useSelector(getVigboLinkState);
    const showVigboEndTariffBanner =
        (vigboLinkState === VigboAccountLinkState.linked || forceVigboLinkedAccount) &&
        storage === EStorageType.vigbo &&
        !isMobile &&
        !isLoading &&
        isLoaded &&
        !isVigboActiveTariff &&
        vigboAlbums.length > 0;

    const vigboSubs = useSelector((state: RootState) => BillingDataSelectors.getSubscriptionsByProductIds(state, vigboTariffs));
    const latestExpireTariff = useMemo(() => {
        const subsWithoutRenew = vigboSubs.filter((sub) => !sub.autorenewal);
        return getLatestExpiredSubscription(subsWithoutRenew);
    }, [vigboSubs]);
    const diffInMs = latestExpireTariff && latestExpireTariff.expires - Date.now();
    const diffInDays = diffInMs && Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const showVigboTariffEndRemindBanner =
        latestExpireTariff &&
        storage === EStorageType.vigbo &&
        !isMobile &&
        !isLoading &&
        isLoaded &&
        diffInDays &&
        diffInDays <= vigboTariffBannerRemindDays;

    const showOverquotaBannerConditions: ConditionsWithData = {
        featureEnabled: overquotaBannerConfig.enabled && !overquotaBackendEnabled,
        isOverquota,
        isNotBiz: !isBizUser,
        isNotOnPrem: !IS_ONPREMISE,
        isNotDocs: !isAllDocuments,
    };

    const { result: showOverquotaBanner, resolvedConditions: overquotaBannerResolvedConditions } =
        checkConditions(showOverquotaBannerConditions);

    overquotaDebugLogger.info('overquotaBanner', {
        notShowVigboEndTariffBanner: !showVigboEndTariffBanner,
        ...overquotaBannerResolvedConditions,
    });

    const showPreOverquotaBannerConditions: ConditionsWithData = {
        preOverquotaBanner: preOverquotaBannerConfig.enable,
        isNotBiz: !isBizUser,
        isNotOnPrem: !IS_ONPREMISE,
        isNotCorp: !isCorp,
        isNotPaidCheck: { check: preQuotaEnablePaid || !isPaidUser, preQuotaEnablePaid, isPaidUser },
        isNotDocs: !isAllDocuments,
        isNotFamilyPage: !isFamilyPage,
        isNotFamilyAlbums: !isFamilyAlbums,
        isNotFamilyFolders: !isFamilyFolders,
        isNotOverquota: !isOverquota,

        overLimitCheck: { check: usedPercent >= preQuotaSizeLimit, usedPercent, preQuotaOfferLimit: preQuotaSizeLimit },
        isPeriodPassed: isPreOverquotaBannerPeriodPassedCheck,
    };

    const { result: showPreOverquotaBanner, resolvedConditions: preOverquotaBannerResolvedConditions } =
        checkConditions(showPreOverquotaBannerConditions);

    preOverquotaDebugLogger.info('preOverquotaBanner', {
        notShowVigboEndTariffBanner: !showVigboEndTariffBanner,
        notShowOverquotaBanner: !showOverquotaBanner,
        notShowVigboTariffEndRemindBanner: !showVigboTariffEndRemindBanner,
        ...preOverquotaBannerResolvedConditions,
    });

    const [duplicatesPromoBannerHidden, setDuplicatesPromoBannerHidden] = useState<boolean>(
        Boolean(dispatch(getDuplicatesPromoBannerHidden()))
    );
    const hasDuplicates = useSelector(getUserHasDuplicates);

    if (!show) {
        return null;
    }

    if (showVigboEndTariffBanner) {
        return <VigboEndTariffBanner />;
    }

    if (showOverquotaBanner) {
        return <OverquotaBanner />;
    }

    if (showVigboTariffEndRemindBanner) {
        return <VigboRenewRemindBanner tariff={latestExpireTariff} />;
    }

    if (showPreOverquotaBanner) {
        return <PreOverquotaBanner setShow={setShow} />;
    }

    const showFreemiumConnectedBannerConditions: ConditionsWithData = {
        freemiumConnectedBanner,
        isSoftOrHardFreemium: isSoftFreemium || isHardFreemium,
        isFreemium,
        isHome,
        isNotOverquota: !isOverquota,
        isNotBiz: !isBizUser,
        isNotCorp: !isCorp,
        notShowVigboEndTariffBanner: !showVigboEndTariffBanner,
        notShowOverquotaBanner: !showOverquotaBanner,
        notShowVigboTariffEndRemindBanner: !showVigboTariffEndRemindBanner,
        notShowPreOverquotaBanner: !showPreOverquotaBanner,
    };

    const { result: showFreemiumConnectedBanner, resolvedConditions: freemiumConnectedBannerResolvedConditions } = checkConditions(
        showFreemiumConnectedBannerConditions
    );

    freemiumDebugLogger.info('freemiumConnectedBanner', freemiumConnectedBannerResolvedConditions);

    if (showFreemiumConnectedBanner) {
        return <FreemiumConnectedBanner />;
    }

    const showDuplicatesPromoBanner = duplicatesCheckAllConditionsTrue('duplicatesConnectedBanner', {
        isDuplicatesEnabled: IS_DUPLICATES_ENABLED,
        isDuplicatesBannerEnabled: duplicatesBanner,
        isNotHidden: !duplicatesPromoBannerHidden,
        isPageAllowed: isHome || isAlbums || isDuplicates,
        isNotBiz: !isBizUser,
        isNotOnPrem: !IS_ONPREMISE,
        isNotCorp: !isCorp,
        isNotPaid: !isPaidUser,
        isNotOverquota: !isOverquota,
        hasDuplicates,
    });

    if (showDuplicatesPromoBanner) {
        return <DuplicatesPromoBanner setHidden={setDuplicatesPromoBannerHidden} />;
    }

    return null;
};
